import clsx from 'clsx';
import { useId, useMemo, useState, type ReactNode } from 'react';
import { Btn } from '@ui/actions';
import { Select } from '@ui/forms';
import { FaIconArrowDown19, FaIconArrowDownAZ, FaIconArrowUp91, FaIconArrowUpZA } from '@ui/icons-fa';
import { DataTableSortableRoot, DataTableSortableTbodyRows, useDataTableSortableSensors } from './DataTableSortableRows';
import type { DataTableSortableAccessibility, SortableRowAriaOptions } from './DataTableSortableRows';
import { dataTableStickyColumnClass, type DataTableSticky } from './dataTableColumnUtils';

export type SortDirection = 'asc' | 'desc';
export type SortType = 'alpha' | 'numeric';
export type DataTableAlign = 'left' | 'center' | 'right';
/** Wrapper / header corner radius. `square` is flush (no rounded corners). */
export type DataTableCorners = 'rounded' | 'square';
export type { DataTableSticky };

export interface DataTableColumn<T> {
    /** Column id. Use `'actions'` for the icon-button column — DataTable pins it on `--layout-wide-up`. */
    id: string;
    header: ReactNode;
    /** Plain-text label for screen readers when `header` is not a string. */
    headerLabel?: string;
    renderCell: (row: T) => ReactNode;
    sortable?: boolean;
    sortType?: SortType;
    sortValue?: (row: T) => string | number;
    thClassName?: string;
    tdClassName?: string;
    align?: DataTableAlign;
    /**
     * Pin the column while the table scrolls horizontally (`--layout-wide-up`).
     * `'start'` sticks left; `'end'` sticks right. `id: 'actions'` defaults to `'end'`.
     */
    sticky?: DataTableSticky;
    renderFilter?: () => ReactNode;
    /** Number of header columns this filter cell spans. Defaults to 1. */
    filterColSpan?: number;
    filterClassName?: string;
    /** Class on the filter `<tr>` when this column's filter is stacked (`filterStackFullWidth`). */
    filterRowClassName?: string;
    /** Describes the filter control for assistive tech when `filterable` is enabled. */
    filterLabel?: string;
    /** Full-width title field in the mobile card layout (no label). */
    mobileCardPrimary?: boolean;
    /** Groups the cell on the bottom action row of the mobile card. */
    mobileCardAction?: boolean;
    /** Hides the visible mobile card label. */
    mobileHideLabel?: boolean;
    /** Stacks the mobile card label above the cell value instead of side-by-side. */
    mobileCardStacked?: boolean;
}

export interface DataTableProps<T> {
    columns: DataTableColumn<T>[];
    rows: T[];
    getRowKey: (row: T) => string;
    tableClassName?: string;
    className?: string;
    theadClassName?: string;
    tbodyClassName?: string;
    tfootClassName?: string;
    headerRowClassName?: string;
    headerCellClassName?: string;
    filterRowClassName?: string;
    rowClassName?: string | ((row: T, index: number) => string | undefined);
    filterable?: boolean;
    /** Hides the column header row when `rows` is empty (filter row still renders). */
    hideHeaderWhenEmpty?: boolean;
    /** Stacks each filter control on its own full-width row (one cell per `renderFilter`, each spanning all columns). */
    filterStackFullWidth?: boolean;
    /** Renders body rows as stacked cards on mobile instead of a horizontal table. */
    mobileCardLayout?: boolean;
    /** Wrapper and header radius. Use `square` when nested inside an already-rounded panel. */
    corners?: DataTableCorners;
    scrollable?: boolean;
    maxBodyHeight?: string;
    tfoot?: ReactNode;
    emptyState?: ReactNode;
    /** Visible or visually hidden table caption for assistive tech. */
    caption?: string;
    captionVisuallyHidden?: boolean;
    /** Names the table when no visible caption is provided. */
    ariaLabel?: string;
    /** Names the scrollable wrapper region when `scrollable` is enabled. */
    scrollRegionLabel?: string;
    defaultSortColumnId?: string;
    defaultSortDirection?: SortDirection;
    sortColumnId?: string;
    sortDirection?: SortDirection;
    onSortChange?: (columnId: string, direction: SortDirection) => void;
    onRowClick?: (row: T, index: number) => void;
    /** Row keys (from `getRowKey`) that can be reordered via drag-and-drop within the table body. */
    sortableRowIds?: string[];
    onSortableRowDragEnd?: (activeId: string, overId: string) => void;
    /** `id` of visually hidden text describing keyboard reorder (linked via `aria-describedby` on sortable rows). */
    sortableInstructionsId?: string;
    getSortableRowAriaLabel?: (options: SortableRowAriaOptions<T>) => string;
    sortableAccessibility?: DataTableSortableAccessibility;
}

const alignClass = (align?: DataTableAlign) => {
    if (align === 'center') {
        return 'data-table-align-center';
    }

    if (align === 'right') {
        return 'data-table-align-right';
    }

    return 'data-table-align-left';
};

const getColumnLabel = <T,>(column: DataTableColumn<T>) => {
    if (column.headerLabel) {
        return column.headerLabel;
    }

    if (typeof column.header === 'string' || typeof column.header === 'number') {
        return String(column.header);
    }

    return column.id;
};

const getSortButtonLabel = (columnLabel: string, isSorted: boolean, direction: SortDirection) => {
    if (!isSorted) {
        return `Sort by ${columnLabel}`;
    }

    const currentOrder = direction === 'asc' ? 'ascending' : 'descending';
    const nextOrder = direction === 'asc' ? 'descending' : 'ascending';

    return `${columnLabel}, sorted ${currentOrder}. Activate to sort ${nextOrder}.`;
};

const getNextSortDirection = (isSorted: boolean, direction: SortDirection): SortDirection => {
    return isSorted && direction === 'asc' ? 'desc' : 'asc';
};

const getSortIndicatorLabel = (sortType: SortType, direction: SortDirection) => {
    if (sortType === 'numeric') {
        return direction === 'asc' ? 'Filter Numeric 1-9' : 'Filter Numeric 9-1';
    }

    return direction === 'asc' ? 'Filter Alphabetical A-Z' : 'Filter Alphabetical Z-A';
};

const SortIcon = ({ sortType, direction }: { sortType: SortType; direction: SortDirection }) => {
    if (sortType === 'numeric') {
        return direction === 'asc' ? (
            <FaIconArrowUp91 iconClasses="data-table-sort-icon" />
        ) : (
            <FaIconArrowDown19 iconClasses="data-table-sort-icon" />
        );
    }

    return direction === 'asc' ? (
        <FaIconArrowUpZA iconClasses="data-table-sort-icon" />
    ) : (
        <FaIconArrowDownAZ iconClasses="data-table-sort-icon" />
    );
};

const compareRows = <T,>(
    a: T,
    b: T,
    sortValue: (row: T) => string | number,
    sortType: SortType,
    direction: SortDirection,
) => {
    const aVal = sortValue(a);
    const bVal = sortValue(b);
    let result = 0;

    if (sortType === 'numeric') {
        const aNum = typeof aVal === 'number' ? aVal : Number(aVal);
        const bNum = typeof bVal === 'number' ? bVal : Number(bVal);
        result = aNum - bNum;
    } else {
        result = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
    }

    return direction === 'desc' ? -result : result;
};

const buildFilterCells = <T,>(columns: DataTableColumn<T>[], stackFullWidth = false) => {
    const cells: Array<{
        key: string;
        column: DataTableColumn<T>;
        colSpan: number;
        spannedColumns: DataTableColumn<T>[];
    }> = [];

    if (stackFullWidth) {
        return columns
            .filter((column) => column.renderFilter)
            .map((column) => ({
                key: `${column.id}-filter`,
                column,
                colSpan: columns.length,
                spannedColumns: columns,
            }));
    }

    let columnIndex = 0;

    while (columnIndex < columns.length) {
        const column = columns[columnIndex];
        const requestedColSpan = column.filterColSpan ?? 1;
        const colSpan = Math.min(requestedColSpan, columns.length - columnIndex);
        const spannedColumns = columns.slice(columnIndex, columnIndex + colSpan);

        cells.push({
            key: `${column.id}-filter`,
            column,
            colSpan,
            spannedColumns,
        });

        columnIndex += colSpan;
    }

    return cells;
};

const DataTable = <T,>({
    columns,
    rows,
    getRowKey,
    tableClassName,
    className,
    theadClassName,
    tbodyClassName,
    tfootClassName,
    headerRowClassName,
    headerCellClassName,
    filterRowClassName,
    rowClassName,
    filterable = false,
    hideHeaderWhenEmpty = false,
    filterStackFullWidth = false,
    mobileCardLayout = true,
    corners = 'rounded',
    scrollable = false,
    maxBodyHeight,
    tfoot,
    emptyState,
    caption,
    captionVisuallyHidden = false,
    ariaLabel,
    scrollRegionLabel,
    defaultSortColumnId,
    defaultSortDirection = 'asc',
    sortColumnId,
    sortDirection,
    onSortChange,
    onRowClick,
    sortableRowIds,
    onSortableRowDragEnd,
    sortableInstructionsId,
    getSortableRowAriaLabel,
    sortableAccessibility,
}: DataTableProps<T>) => {
    const tableInstanceId = useId();
    const columnHeaderId = (columnId: string) => `${tableInstanceId}-col-${columnId}`;
    const isControlled = sortColumnId !== undefined && onSortChange !== undefined;
    const [internalSortColumnId, setInternalSortColumnId] = useState<string | undefined>(defaultSortColumnId);
    const [internalSortDirection, setInternalSortDirection] = useState<SortDirection>(defaultSortDirection);

    const activeSortColumnId = isControlled ? sortColumnId : internalSortColumnId;
    const activeSortDirection = isControlled ? (sortDirection ?? 'asc') : internalSortDirection;

    const handleSort = (column: DataTableColumn<T>) => {
        if (!column.sortable) {
            return;
        }

        const isSameColumn = activeSortColumnId === column.id;
        const nextDirection = getNextSortDirection(isSameColumn, activeSortDirection);

        if (isControlled) {
            onSortChange(column.id, nextDirection);
            return;
        }

        setInternalSortColumnId(column.id);
        setInternalSortDirection(nextDirection);
    };

    const displayRows = useMemo(() => {
        if (isControlled || !activeSortColumnId) {
            return rows;
        }

        const column = columns.find((entry) => entry.id === activeSortColumnId);
        if (!column?.sortable || !column.sortValue) {
            return rows;
        }

        const sortType = column.sortType ?? 'alpha';

        return [...rows].sort((a, b) => compareRows(a, b, column.sortValue!, sortType, activeSortDirection));
    }, [activeSortColumnId, activeSortDirection, columns, isControlled, rows]);

    const filterCells = useMemo(
        () => (filterable ? buildFilterCells(columns, filterStackFullWidth) : []),
        [columns, filterable, filterStackFullWidth],
    );
    const sortableColumns = useMemo(
        () => columns.filter((column) => column.sortable && column.sortValue),
        [columns],
    );
    const showHeaderRow = !hideHeaderWhenEmpty || displayRows.length > 0;
    const showMobileSortToolbar = mobileCardLayout && showHeaderRow && sortableColumns.length > 0;
    const mobileSortColumnId =
        activeSortColumnId ?? defaultSortColumnId ?? sortableColumns[0]?.id ?? '';
    const mobileSortOptions = useMemo(
        () =>
            sortableColumns.map((column) => ({
                value: column.id,
                label: getColumnLabel(column),
            })),
        [sortableColumns],
    );
    const isMobileSortDescending =
        activeSortColumnId === mobileSortColumnId && activeSortDirection === 'desc';

    const applySort = (columnId: string, direction: SortDirection) => {
        if (isControlled) {
            onSortChange?.(columnId, direction);
            return;
        }

        setInternalSortColumnId(columnId);
        setInternalSortDirection(direction);
    };

    const handleMobileSortColumnChange = (columnId: string) => {
        const column = columns.find((entry) => entry.id === columnId);
        if (!column?.sortable) {
            return;
        }

        const direction = columnId === activeSortColumnId ? activeSortDirection : 'asc';
        applySort(columnId, direction);
    };

    const handleMobileSortDirectionToggle = () => {
        if (!mobileSortColumnId) {
            return;
        }

        const column = columns.find((entry) => entry.id === mobileSortColumnId);
        if (!column?.sortable) {
            return;
        }

        const isActiveColumn = activeSortColumnId === mobileSortColumnId;
        const nextDirection: SortDirection =
            isActiveColumn && activeSortDirection === 'asc' ? 'desc' : 'asc';
        applySort(mobileSortColumnId, nextDirection);
    };

    const renderSortButton = (column: DataTableColumn<T>) => {
        const isSorted = activeSortColumnId === column.id;
        const sortType = column.sortType ?? 'alpha';
        const columnLabel = getColumnLabel(column);
        const nextSortDirection = getNextSortDirection(isSorted, activeSortDirection);
        const sortIndicatorLabel = getSortIndicatorLabel(sortType, nextSortDirection);

        return (
            <button
                type="button"
                className={clsx('data-table-sort', isSorted && 'is-sorted')}
                aria-label={getSortButtonLabel(columnLabel, isSorted, activeSortDirection)}
                title={sortIndicatorLabel}
                aria-pressed={isSorted}
                onClick={() => handleSort(column)}
            >
                <span className="data-table-sort-label" aria-hidden="true">
                    {column.header}
                </span>
                <span
                    className={clsx('data-table-sort-icon-wrap', !isSorted && 'is-inactive')}
                    aria-hidden="true"
                >
                    <SortIcon sortType={sortType} direction={isSorted ? activeSortDirection : 'asc'} />
                </span>
            </button>
        );
    };

    const wrapperStyle = maxBodyHeight
        ? ({ ['--data-table-max-height' as string]: maxBodyHeight } as const)
        : undefined;
    const tableLabel = caption ?? ariaLabel;
    const scrollLabel = scrollRegionLabel ?? tableLabel ?? 'Data table';
    const sortableSensors = useDataTableSortableSensors();
    const isSortableTable = Boolean(
        sortableRowIds && sortableRowIds.length > 0 && onSortableRowDragEnd && displayRows.length > 0,
    );

    const handleSortableDragEnd = (event: { active: { id: unknown }; over: { id: unknown } | null }) => {
        const { active, over } = event;
        if (!over || active.id === over.id || !onSortableRowDragEnd) {
            return;
        }
        onSortableRowDragEnd(String(active.id), String(over.id));
    };

    const tbodyContent =
        displayRows.length === 0 ? (
            <tr className="data-table-empty-row">
                <td colSpan={columns.length} role="status" aria-live="polite">
                    {emptyState ?? 'No results.'}
                </td>
            </tr>
        ) : isSortableTable ? (
            <DataTableSortableTbodyRows
                sortableRowIds={sortableRowIds!}
                rows={displayRows}
                columns={columns}
                getRowKey={getRowKey}
                columnHeaderId={columnHeaderId}
                rowClassName={rowClassName}
                onRowClick={onRowClick}
                sortableInstructionsId={sortableInstructionsId}
                getSortableRowAriaLabel={getSortableRowAriaLabel}
            />
        ) : (
            displayRows.map((row, index) => (
                <tr
                    key={getRowKey(row)}
                    className={clsx(
                        typeof rowClassName === 'function' ? rowClassName(row, index) : rowClassName,
                        onRowClick && 'is-clickable',
                    )}
                    tabIndex={onRowClick ? 0 : undefined}
                    onClick={onRowClick ? () => onRowClick(row, index) : undefined}
                    onKeyDown={
                        onRowClick
                            ? (event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                      event.preventDefault();
                                      onRowClick(row, index);
                                  }
                              }
                            : undefined
                    }
                >
                    {columns.map((column) => {
                        const columnLabel = getColumnLabel(column);

                        return (
                            <td
                                key={`${getRowKey(row)}-${column.id}`}
                                headers={columnHeaderId(column.id)}
                                data-label={columnLabel}
                                className={clsx(
                                    alignClass(column.align),
                                    column.tdClassName,
                                    dataTableStickyColumnClass(column),
                                    column.mobileCardPrimary && 'data-table-mobile-primary',
                                    column.mobileCardAction && 'data-table-mobile-action',
                                    column.mobileCardStacked && 'data-table-mobile-stacked',
                                    (column.mobileHideLabel || column.mobileCardPrimary) &&
                                        'data-table-mobile-no-label',
                                )}
                            >
                                {column.renderCell(row)}
                            </td>
                        );
                    })}
                </tr>
            ))
        );

    const tableElement = (
        <table
            className={clsx('data-table', tableClassName, mobileCardLayout && 'is-mobile-cards')}
            {...(!caption && ariaLabel ? { 'aria-label': ariaLabel } : {})}
        >
                {caption && (
                    <caption className={clsx(captionVisuallyHidden && 'data-table-visually-hidden')}>{caption}</caption>
                )}
                <thead className={theadClassName}>
                    {filterable &&
                        (() => {
                            const renderFilterCell = ({
                                key,
                                column,
                                colSpan,
                                spannedColumns,
                            }: (typeof filterCells)[number]) => {
                                const columnLabel = getColumnLabel(column);
                                const filterLabel =
                                    column.filterLabel ??
                                    (colSpan > 1
                                        ? `Filter ${spannedColumns.map((entry) => getColumnLabel(entry)).join(', ')}`
                                        : `Filter ${columnLabel}`);
                                const filterLabelId = `${columnHeaderId(column.id)}-filter`;

                                return (
                                    <td
                                        key={key}
                                        colSpan={colSpan}
                                        {...(showHeaderRow
                                            ? {
                                                  headers: spannedColumns
                                                      .map((entry) => columnHeaderId(entry.id))
                                                      .join(' '),
                                              }
                                            : {})}
                                        className={clsx(
                                            'data-table-filter-cell',
                                            alignClass(column.align),
                                            column.filterClassName ?? column.thClassName,
                                            dataTableStickyColumnClass(column),
                                        )}
                                    >
                                        <span id={filterLabelId} className="data-table-visually-hidden">
                                            {filterLabel}
                                        </span>
                                        {column.renderFilter?.()}
                                    </td>
                                );
                            };

                            // Stacked: one full-width filter per row so each control sits on its own line.
                            if (filterStackFullWidth) {
                                return filterCells.map((cell) => (
                                    <tr
                                        key={`${cell.key}-row`}
                                        className={clsx(
                                            'filterable',
                                            filterRowClassName,
                                            cell.column.filterRowClassName,
                                        )}
                                    >
                                        {renderFilterCell(cell)}
                                    </tr>
                                ));
                            }

                            return (
                                <tr className={clsx('filterable', filterRowClassName)}>
                                    {filterCells.map(renderFilterCell)}
                                </tr>
                            );
                        })()}
                    {showMobileSortToolbar && (
                        <tr className="data-table-mobile-sort-row">
                            <td colSpan={columns.length}>
                                <div className="data-table-mobile-sort" role="toolbar" aria-label="Sort table">
                                    <span className="data-table-mobile-sort-label" id={`${tableInstanceId}-mobile-sort-label`}>
                                        Sort by:
                                    </span>
                                    <Select
                                        className="data-table-mobile-sort-select"
                                        options={mobileSortOptions}
                                        value={mobileSortColumnId}
                                        onChange={handleMobileSortColumnChange}
                                        ariaLabel="Sort by column"
                                        ariaDescribedBy={`${tableInstanceId}-mobile-sort-label`}
                                        wrapDropdownInCard={false}
                                    />
                                    <Btn
                                        variant="secondary-outlined"
                                        buttonSize="sm"
                                        buttonClasses="data-table-mobile-sort-direction"
                                        buttonText={isMobileSortDescending ? 'Desc ↓' : 'Asc ↑'}
                                        ariaLabel={
                                            isMobileSortDescending
                                                ? 'Sorted descending. Activate to sort ascending.'
                                                : 'Sorted ascending. Activate to sort descending.'
                                        }
                                        buttonOnClick={handleMobileSortDirectionToggle}
                                    />
                                </div>
                            </td>
                        </tr>
                    )}
                    {showHeaderRow && (
                        <tr className={headerRowClassName}>
                            {columns.map((column) => {
                                const isSorted = activeSortColumnId === column.id;

                                return (
                                    <th
                                        key={column.id}
                                        id={columnHeaderId(column.id)}
                                        scope="col"
                                        className={clsx(
                                            alignClass(column.align),
                                            headerCellClassName,
                                            column.thClassName,
                                            dataTableStickyColumnClass(column),
                                        )}
                                        aria-sort={
                                            column.sortable && isSorted
                                                ? activeSortDirection === 'asc'
                                                    ? 'ascending'
                                                    : 'descending'
                                                : column.sortable
                                                  ? 'none'
                                                  : undefined
                                        }
                                    >
                                        {column.sortable ? (
                                            renderSortButton(column)
                                        ) : (
                                            column.header
                                        )}
                                    </th>
                                );
                            })}
                        </tr>
                    )}
                </thead>
                <tbody className={tbodyClassName}>{tbodyContent}</tbody>
                {tfoot && <tfoot className={tfootClassName}>{tfoot}</tfoot>}
            </table>
    );

    return (
        <div
            className={clsx(
                'data-table-wrapper',
                corners === 'square' && 'data-table-square',
                scrollable && 'is-scrollable',
                filterable && 'filterable-header',
                className,
            )}
            style={wrapperStyle}
            {...(scrollable
                ? {
                      role: 'region',
                      tabIndex: 0,
                      'aria-label': scrollLabel,
                  }
                : {})}
        >
            {isSortableTable ? (
                <DataTableSortableRoot
                    sensors={sortableSensors}
                    onDragEnd={handleSortableDragEnd}
                    accessibility={sortableAccessibility}
                >
                    {tableElement}
                </DataTableSortableRoot>
            ) : (
                tableElement
            )}
        </div>
    );
};

export default DataTable;
